import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showAlert } from "../utils/alert";
import { useAuth } from "../context/AuthContext";
import {verifyAndResetPassword,requestResetCode} from "../services/api"
const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: false,
    // Campos para recuperación
    verificationCode: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Pasos de recuperación: false = login normal, 1 = solicitar código, 2 = verificar y cambiar
  const [recoveryStep, setRecoveryStep] = useState<false | 1 | 2>(false);

  const navigate = useNavigate();
  const { login, initialToken, isInitializing } = useAuth();

  useEffect(() => {
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    if (rememberedUsername) {
      setFormData((prev) => ({
        ...prev,
        username: rememberedUsername,
        remember: true,
      }));
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  // Manejo del login normal
  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { username, password, remember } = formData;

    if (!username || !password) {
      showAlert("Por favor, ingrese usuario y contraseña.", "warning");
      return;
    }

    if (!initialToken) {
      showAlert(
        "Error al obtener el token inicial. Por favor, intente de nuevo.",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const loginSuccess = await login(username, password);
      if (loginSuccess) {
        if (remember) {
          localStorage.setItem("rememberedUsername", username);
        } else {
          localStorage.removeItem("rememberedUsername");
        }
        showAlert("Inicio de sesión exitoso!", "success");
        navigate("/");
      }
    } catch (error) {
      showAlert(
        error instanceof Error ? error.message : "Error en el inicio de sesión",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Paso 1: Solicitar código de verificación
  const handleRequestCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { username } = formData;

    if (!username) {
      showAlert("Por favor, ingrese su número de documento.", "warning");
      return;
    }

    if (!initialToken) {
      showAlert(
        "Error al obtener el token inicial. Por favor, intente de nuevo.",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await requestResetCode(username, initialToken);
      if (response.response === "1") {
        showAlert(response.data.msg, "success");
        setRecoveryStep(2); // Avanzar al paso de verificación
      }
    } catch (error) {
      // handleApiError ya maneja la muestra de alertas
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Paso 2: Verificar código y cambiar contraseña
  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { username, verificationCode, newPassword, confirmPassword } =
      formData;

    if (!verificationCode || !newPassword || !confirmPassword) {
      showAlert("Por favor complete todos los campos.", "warning");
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlert("Las contraseñas no coinciden.", "warning");
      return;
    }

    if (!initialToken) {
      showAlert(
        "Error al obtener el token inicial. Por favor, intente de nuevo.",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await verifyAndResetPassword(
        username,
        verificationCode,
        newPassword,
        confirmPassword,
        initialToken
      );

      if (response.response === "1") {
        showAlert(response.data.msg, "success");
        setRecoveryStep(false); // Volver al login
        setFormData((prev) => ({
          ...prev,
          password: "",
          verificationCode: "",
          newPassword: "",
          confirmPassword: "",
        }));
      }
    } catch (error) {
      // handleApiError ya maneja la muestra de alertas
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex h-screen items-center justify-center  ">
        <div className="text-white">Inicializando...</div>
      </div>
    );
  }

  return (
    <main className="flex h-screen flex-col items-center justify-center ">
      <div className="z-10 w-full max-w-sm px-12">
        <img
          src="/logo.png"
          alt="logo"
          className="mb-4 w-50 h-50 mx-auto object-contain"
        />
        <h1 className="text-white text-xl font-bold text-center pb-2">
          {!recoveryStep
            ? "Administrador"
            : recoveryStep === 1
            ? "Recuperar Contraseña"
            : "Cambiar Contraseña"}
        </h1>

        {!recoveryStep ? (
          // Formulario de Login Normal
          <form
            className="flex flex-col gap-5 pt-5"
            onSubmit={handleLoginSubmit}
          >
            <input
              type="text"
              name="username"
              placeholder="Usuario"
              className="text-black p-2 rounded-md focus:outline-none focus:ring-4 focus:ring-pink-800 focus:border-pink-800"
              value={formData.username}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <input
              type="password"
              name="password"
              placeholder="Contraseña"
              className="text-black p-2 rounded-md focus:outline-none focus:ring-4 focus:ring-pink-800 focus:border-pink-800"
              value={formData.password}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <div className="flex items-center gap-2 justify-center">
              <input
                type="checkbox"
                name="remember"
                id="remember"
                className="form-checkbox text-yellow-500"
                checked={formData.remember}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <label htmlFor="remember" className="text-white">
                Recordar mis datos
              </label>
            </div>
            <button
              type="submit"
              className={`bg-yellow-500 text-white p-2 rounded-md transition-colors flex items-center justify-center h-10 ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-yellow-600"
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Cargando..." : "Ingresar"}
            </button>
            <button
              type="button"
              onClick={() => setRecoveryStep(1)}
              className="text-white text-sm hover:underline mt-2"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </form>
        ) : recoveryStep === 1 ? (
          // Formulario de Solicitud de Código
          <form
            className="flex flex-col gap-5 pt-5"
            onSubmit={handleRequestCode}
          >
            <input
              type="text"
              name="username"
              placeholder="Número de Documento"
              className="text-black p-2 rounded-md focus:outline-none focus:ring-4 focus:ring-pink-800 focus:border-pink-800"
              value={formData.username}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className={`bg-yellow-500 text-white p-2 rounded-md transition-colors flex items-center justify-center h-10 ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-yellow-600"
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Solicitar Código"}
            </button>
            <button
              type="button"
              onClick={() => setRecoveryStep(false)}
              className="bg-gray-500 text-white p-2 rounded-md transition-colors hover:bg-gray-600"
            >
              Volver al Login
            </button>
          </form>
        ) : (
          // Formulario de Verificación y Cambio de Contraseña
          <form
            className="flex flex-col gap-5 pt-5"
            onSubmit={handlePasswordReset}
          >
            <input
              type="text"
              name="verificationCode"
              placeholder="Código de Verificación"
              className="text-black p-2 rounded-md focus:outline-none focus:ring-4 focus:ring-pink-800 focus:border-pink-800"
              value={formData.verificationCode}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <input
              type="password"
              name="newPassword"
              placeholder="Nueva Contraseña"
              className="text-black p-2 rounded-md focus:outline-none focus:ring-4 focus:ring-pink-800 focus:border-pink-800"
              value={formData.newPassword}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirmar Contraseña"
              className="text-black p-2 rounded-md focus:outline-none focus:ring-4 focus:ring-pink-800 focus:border-pink-800"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className={`bg-yellow-500 text-white p-2 rounded-md transition-colors flex items-center justify-center h-10 ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-yellow-600"
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Procesando..." : "Cambiar Contraseña"}
            </button>
            <button
              type="button"
              onClick={() => setRecoveryStep(1)}
              className="bg-gray-500 text-white p-2 rounded-md transition-colors hover:bg-gray-600"
            >
              Volver
            </button>
          </form>
        )}
      </div>
    </main>
  );
};

export default Login;
// src/pages/Login.tsx
